import { useAuth, useUser } from '@clerk/clerk-react';
import { readProgress, TourChecklist, TourHost, withDeckHidden, type TourStep } from '@flobi/asset-library/tours';
import '@flobi/asset-library/tours.css';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme';
import { usePhone } from '../../ui/usePhone';
import { completeStep, loadSteps, loadViewPrefs, resetOnboarding, saveViewPref, useOnboarding } from './tourData';
import { PAGES } from './tourPages';
import { PICTURES, toneOf } from './pictureScenes';
import { PROGRESS_KEY, tourForStep } from './tours';
import { TOUR_ANCHORS } from './tourAnchors';

/** What one chapter hands the next: the file the editor's chapters run on. */
const carried = (vars: Record<string, unknown> = {}) => (typeof vars.filePath === 'string' ? { filePath: vars.filePath } : {});

// The paused pill sits bottom left, above the zoom pill: bottom centre is the dock.
const inCorner = () => true;

const LEFT_OPEN = '[role="dialog"], [role="menu"]';
// The panels and bars a step opens, by their own close buttons: Assets (Generate too), Animate,
// Layers, Properties (26 Oct: "close popups and sheets when moving to next step").
const PANEL_CLOSERS = ['Close Assets', 'Close Animate', 'Close Layers', 'Close Properties'].map((name) => `button[aria-label="${name}"]`).join(', ');
function closeLeftOpen() {
  if (typeof document === 'undefined') return;
  for (let i = 0; i < 4; i += 1) {
    const open = [...document.querySelectorAll(LEFT_OPEN)].filter((el) => !el.closest('.ft-layer'));
    if (!open.length) break;
    document.body.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true }));
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>(PANEL_CLOSERS)) button.click();
}

const FINALE = { title: 'You know all of Artwork', body: 'Tips on Home keeps a short guide to everything else.', picture: PICTURES.done?.() };

/**
 * Artwork's tours, run by the kit's TourHost (@flobi/asset-library/tours), and the Get started steps
 * they tick, listed in the lessons card in the corner (TourChecklist: it opens by itself on Home,
 * ArtworkGetStarted, until every step is done or it's closed; the title menu's Learn Artwork and Tips
 * open it again). Signed-in people only: progress and credits need an account. Mounted once beside
 * the hints (App.tsx). Phones get the deck and its links, not the rings (yet).
 *
 * What a step left open closes when the next one comes up or the tour ends (26 Oct: "close popups
 * when I finish a step", then "and sheets"): popovers and menus by a press on the page outside them
 * (how Artwork's own close, ui/popover.tsx), then Assets, Animate, Layers and Properties by their own
 * close buttons. Not with an Escape, as Handoff does: here Escape also lets go of what is chosen on the
 * canvas, which the next step may point at. A step that works inside one says `keepOpen`.
 */
export function ArtworkTourHost() {
  const { isSignedIn, isLoaded } = useAuth();
  const phone = usePhone();
  const on = Boolean(isSignedIn);

  // The steps and the saved progress, read once signed in (the deck and the menu row read them too).
  useEffect(() => {
    if (!isLoaded) return;
    if (!on) {
      resetOnboarding();
      return;
    }
    void loadSteps();
    void loadViewPrefs();
  }, [on, isLoaded]);

  return on && !phone ? <Tours /> : null;
}

function Tours() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useUser();
  const { steps, viewPrefs } = useOnboarding();
  const progress = useMemo(() => readProgress(viewPrefs, PROGRESS_KEY), [viewPrefs]);
  const saveProgress = useCallback((next: unknown) => void saveViewPref(PROGRESS_KEY, next), []);
  const go = useCallback((path: string) => navigate(path), [navigate]);
  const tick = useCallback((key: string) => void completeStep(key), []);
  const onStepLive = useCallback(({ step }: { step: TourStep }) => {
    if (!step.keepOpen) closeLeftOpen();
  }, []);

  const mode = theme === 'light' ? 'light' : 'dark';
  return (
    <>
      <TourHost
        app="artwork"
        mode={mode}
        pathname={pathname}
        navigate={go}
        pages={PAGES}
        progress={progress}
        saveProgress={saveProgress}
        remember={['filePath']}
        completeStep={tick}
        homeSteps={steps}
        pictures={PICTURES}
        toneOf={toneOf}
        carry={carried}
        pillCorner={inCorner}
        finale={FINALE}
        onStepLive={onStepLive}
        onFinish={closeLeftOpen}
      />
      {/* The lessons card, bottom right above the Agent orb (Mohamad, 30 Oct: in place of Home's
          "New to Artwork?" card and its Learn Artwork row). */}
      <TourChecklist
        app="artwork"
        appName="Artwork"
        mode={mode}
        name={user?.firstName || ''}
        steps={steps}
        tourForStep={tourForStep}
        onRoute={go}
        skippedSteps={progress.skippedSteps}
        closed={Boolean(progress.hidden)}
        onClose={() => saveProgress(withDeckHidden(progress))}
        onMore={() => go('/tips')}
        anchor={TOUR_ANCHORS.getStarted}
      />
    </>
  );
}
